// RowList (Components, BL-189 consolidation): things one a row on hairlines, the sample's .item row
// (DESIGN.md §3 and §4; kit.css .item; base.css's .flw-rows). A row is its name (15 / 600), a state word
// beside it, a muted aside after it, lines under it, and one thing at its end: a state word, an action
// (Link), a figure (an amount) or a muted aside. A muted word before the name can say what kind of thing
// it is (Agent, AI tool). A row whose work waits on someone has its actions on its right: Links, the
// first at rest (or two) and the rest under More, a Menu (§3: at most two in a zone); its end then sits
// at the end of the name's line. Lines can be a state word, a bulleted list or a part of their own (the
// deposits on an estimate). A row with a bar (the catalogued Meter: from call to invoice, the spend by
// platform, revenue by day) draws it between the name and its end, the lines under all three. A row
// that's off (retired, cancelled) is muted; the one that's marked (the technician whose day shows) has a
// heavier rule down its side. Five rows, then Show all, then the next page (§3). It draws ready-to-show
// words: what a holiday, a month or a technician says is the caller's (rows/). Reads only --rowlist-*;
// PenKey, Link, Menu and Meter read their own.
import { useRef, useState, type ReactNode } from 'react';
import { Link } from '../Link/Link.tsx';
import { Menu, type MenuAt } from '../Menu/Menu.tsx';
import { Meter, type MeterProps } from '../Meter/Meter.tsx';
import { PenKey } from '../PenKey/PenKey.tsx';
import type { Pen } from '../pen.ts';
import './RowList.css';

/** A state's word, drawn as DESIGN.md §2 says: ink, muted, dashed under for waiting, red for a problem. */
export type RowTone = 'ink' | 'muted' | 'wait' | 'bad';

/**
 * A line under the name: words, a state word on its own line, a bulleted list (the times they asked
 * for), or a part of its own (the deposits on an estimate).
 */
export type RowLine =
  | {
      text: ReactNode;
      /** muted (the default) is the 13 px secondary line; ink is a line at the row's size. */
      tone?: 'ink' | 'muted';
      /** sm: an ink line at the secondary line's size (what they said, why it waits). */
      size?: 'sm';
      /** Struck through (what a cancelled one was). */
      struck?: boolean;
    }
  | { word: string; tone?: RowTone }
  | { items: readonly string[]; label: string }
  | { part: ReactNode };

/** What's at a row's end: a state word, its one action, a figure (an amount), or a muted aside ("Shown"). */
export type RowEnd =
  | { word: string; tone?: RowTone }
  | { action: string; onPress: () => void; label?: string }
  | { figure: string }
  | { aside: string };

/** One of a row's actions: a Link at rest, or an item under its More. */
export interface RowAction {
  id: string;
  /** "Book it" */
  text: string;
  onPress?: () => void;
  /** A link (tel:, a page) rather than an action. */
  href?: string;
  /** Its name for screen readers, at rest ("Book estimate 212 for Okafor family"). */
  label?: string;
  isDisabled?: boolean;
}

export interface Row {
  id: string;
  /** "Fri, Dec 25", "October 2026", a technician's name. */
  name: ReactNode;
  /** A muted word before the name, what kind of thing it is ("Agent", "AI tool"). */
  kicker?: string;
  /** The name opens the thing (a button); openLabel is its name for screen readers. */
  onOpen?: () => void;
  openLabel?: string;
  /** A technician's pen before the name; null draws the key muted. Omitted, no key. */
  pen?: Pen | null;
  /** A state word beside the name ("Closed", "Running late"). */
  word?: { text: string; tone?: RowTone };
  /** The row's name for screen readers, and its More's ("Okafor family: Asked for another time"). */
  label?: string;
  /** Muted words after the name and its word (", vacation"; sm: " · closed Nov 3"), its own spacing. */
  after?: { text: ReactNode; size?: 'sm' };
  /** Lines under the name. */
  lines?: readonly RowLine[];
  end?: RowEnd;
  /** Its actions on its right: the first `atRest` (1 by default, at most 2) as Links, the rest under More. */
  actions?: readonly RowAction[];
  atRest?: 1 | 2;
  /** A bar between the name and its end (the catalogued Meter); its lines then run under all three. */
  bar?: MeterProps;
  /** Muted: retired, cancelled. */
  off?: boolean;
  /** A heavier rule down its side: the one shown beside the list. */
  marked?: boolean;
}

export interface RowListProps {
  rows: readonly Row[];
  /** Its own title over the rows, where nothing above names it (a view in a chat: "3 things wait for
   * your OK"). */
  title?: string;
  /** A muted line under the title ("1 request waits for someone else’s OK."). */
  sub?: ReactNode;
  /** The list's one way in, under it (a view's Decide them: a Button, or a touch Button in a chat). */
  act?: ReactNode;
  /** The list's name for screen readers ("Equipment"). */
  label?: string;
  /** Shows this many, then "Show all N" (§3: five). Omitted, all of them. */
  first?: number;
  /** The next page, when there is one ("Show more"). */
  onMore?: () => void;
  /**
   * With no rows, the sentence it says between dashed rules. Omitted or null, it draws nothing: a page's
   * region that says its own empty state (ListPage) leaves it out.
   */
  empty?: string | null;
  /** A muted line under the rows ("Only the owner reopens a closed month."). */
  note?: ReactNode;
}

function End({ end }: { end: RowEnd }) {
  if ('figure' in end) return <span className="fl-rowlist__figure">{end.figure}</span>;
  if ('action' in end)
    return (
      <span className="fl-rowlist__end">
        <Link onPress={end.onPress} {...(end.label ? { label: end.label } : {})}>
          {end.action}
        </Link>
      </span>
    );
  if ('aside' in end) return <span className="fl-rowlist__aside">{end.aside}</span>;
  return (
    <span className="fl-rowlist__word" data-tone={end.tone ?? 'ink'}>
      {end.word}
    </span>
  );
}

function Line({ line }: { line: RowLine }) {
  if ('word' in line)
    return (
      <span className="fl-rowlist__line">
        <span className="fl-rowlist__word" data-tone={line.tone ?? 'ink'}>
          {line.word}
        </span>
      </span>
    );
  if ('items' in line)
    return (
      <ul className="fl-rowlist__items" aria-label={line.label}>
        {line.items.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
    );
  if ('part' in line) return <div className="fl-rowlist__part">{line.part}</div>;
  return (
    <span
      className="fl-rowlist__line"
      data-tone={line.tone ?? 'muted'}
      data-size={line.size}
      data-struck={line.struck ? '' : undefined}
    >
      {line.text}
    </span>
  );
}

function ActionLink({ a }: { a: RowAction }) {
  return (
    <Link
      {...(a.href ? { href: a.href } : { onPress: a.onPress })}
      {...(a.label ? { label: a.label } : {})}
      {...(a.isDisabled ? { isDisabled: true } : {})}
    >
      {a.text}
    </Link>
  );
}

/** A row's actions: the first at rest, the rest under More (a Menu under it). */
function Actions({
  actions,
  atRest,
  label,
}: {
  actions: readonly RowAction[];
  atRest: number;
  label: string;
}) {
  const anchor = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState<MenuAt | null>(null);
  const rest = actions.slice(atRest);
  return (
    <span className="fl-rowlist__acts" data-rest={atRest > 1 ? 'two' : undefined}>
      <span className="fl-rowlist__at-rest">
        {actions.slice(0, atRest).map((a) => (
          <ActionLink key={a.id} a={a} />
        ))}
      </span>
      {rest.length ? (
        <span ref={anchor} className="fl-rowlist__more-at">
          <Link
            label={`More for ${label}`}
            onPress={() => setAt(at ? null : anchor.current ? { element: anchor.current } : null)}
          >
            More
          </Link>
          <Menu
            at={at}
            onClose={() => setAt(null)}
            label={`More for ${label}`}
            title="More"
            placement="bottom end"
            sections={[
              {
                items: rest.map((a) => ({
                  id: a.id,
                  label: a.text,
                  ...(a.href ? { href: a.href } : {}),
                  ...(a.isDisabled ? { isDisabled: true } : {}),
                })),
              },
            ]}
            onAction={(id) => rest.find((a) => a.id === id)?.onPress?.()}
          />
        </span>
      ) : null}
    </span>
  );
}

export function RowList({ rows, title, sub, act, label, first, onMore, empty, note }: RowListProps) {
  const [all, setAll] = useState(false);
  if (!rows.length)
    return empty ? (
      <p className="fl-rowlist__empty" role="status">
        {empty}
      </p>
    ) : null;
  const folded = first !== undefined && !all && rows.length > first;
  const shown = folded ? rows.slice(0, first) : rows;
  return (
    <div className="fl-rowlist">
      {title ? <h3 className="fl-rowlist__title">{title}</h3> : null}
      {sub ? <p className="fl-rowlist__sub">{sub}</p> : null}
      <ul className="fl-rowlist__rows" {...(label ? { 'aria-label': label } : {})}>
        {shown.map((r) => {
          const name = r.onOpen ? (
            <button
              type="button"
              className="fl-rowlist__name"
              onClick={r.onOpen}
              {...(r.openLabel ? { 'aria-label': r.openLabel } : {})}
            >
              {r.name}
            </button>
          ) : (
            <b className="fl-rowlist__name">{r.name}</b>
          );
          const acts = r.actions?.length ? r.actions : null;
          return (
            <li
              key={r.id}
              className="fl-rowlist__row"
              {...(r.label ? { 'aria-label': r.label } : {})}
              data-off={r.off ? '' : undefined}
              data-marked={r.marked ? '' : undefined}
              data-acts={acts ? '' : undefined}
              data-bar={r.bar ? '' : undefined}
            >
              <div className="fl-rowlist__main">
                <span className="fl-rowlist__head" data-end={acts && r.end ? '' : undefined}>
                  {r.kicker ? (
                    <>
                      <span className="fl-rowlist__word" data-tone="muted">
                        {r.kicker}
                      </span>{' '}
                    </>
                  ) : null}
                  {r.pen !== undefined ? <PenKey pen={r.pen}>{name}</PenKey> : name}
                  {r.word ? (
                    <span className="fl-rowlist__word" data-tone={r.word.tone ?? 'ink'} data-beside="">
                      {r.word.text}
                    </span>
                  ) : null}
                  {r.after ? (
                    <span className="fl-rowlist__after" data-size={r.after.size}>
                      {r.after.text}
                    </span>
                  ) : null}
                  {acts && r.end ? <End end={r.end} /> : null}
                </span>
                {r.lines?.map((l, i) => (
                  <Line key={i} line={l} />
                ))}
              </div>
              {r.bar ? (
                <span className="fl-rowlist__bar">
                  <Meter {...r.bar} />
                </span>
              ) : null}
              {acts ? (
                <Actions
                  actions={acts}
                  atRest={r.atRest ?? 1}
                  label={r.label ?? (typeof r.name === 'string' ? r.name : 'this row')}
                />
              ) : r.end ? (
                <End end={r.end} />
              ) : null}
            </li>
          );
        })}
      </ul>
      {folded ? (
        <p className="fl-rowlist__more">
          <Link onPress={() => setAll(true)}>{`Show all ${rows.length}`}</Link>
        </p>
      ) : onMore ? (
        <p className="fl-rowlist__more">
          <Link onPress={onMore}>Show more</Link>
        </p>
      ) : null}
      {note ? <p className="fl-rowlist__note">{note}</p> : null}
      {act ? <div className="fl-rowlist__act">{act}</div> : null}
    </div>
  );
}
